import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useReportsHeader } from '../../context/ReportsHeaderContext';
import { normalizeScoreOutOf10 } from '../../utils/hiringRecommendation';

function normalizeCandidateName(session) {
  return (session?.candidate_name ?? session?.name ?? '').trim();
}

function matchSessions(sessions, query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return (sessions ?? []).filter((s) => {
    const name = normalizeCandidateName(s).toLowerCase();
    const email = (s.candidate_email ?? s.email ?? '').toLowerCase();
    const role = (s.job_role ?? s.role ?? '').toLowerCase();
    return name.includes(q) || email.includes(q) || role.includes(q);
  });
}

export default function ReportsHeaderSearch() {
  const { api, searchQuery, setSearchQuery } = useReportsHeader();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);

  const matches = useMemo(
    () => matchSessions(api?.sessions, searchQuery),
    [api?.sessions, searchQuery]
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [searchQuery, matches.length]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectSession = (session) => {
    const id = session?.id ?? session?.session_id;
    if (id != null && api?.selectSession) {
      api.selectSession(id);
      setOpen(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && matches.length > 0) {
      e.preventDefault();
      selectSession(matches[activeIndex] ?? matches[0]);
    }
  };

  if (!api?.sessions) return null;

  return (
    <div ref={rootRef} style={{ position: 'relative', width: 260, flexShrink: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 34,
          padding: '0 10px 0 12px',
          borderRadius: 8,
          border: '1px solid #E2E8F0',
          background: '#fff',
          boxShadow: open ? '0 4px 12px rgba(15,23,42,.08)' : 'none',
        }}
      >
        <span style={{ fontSize: 13, color: '#94A3B8', lineHeight: 1 }} aria-hidden>
          🔍
        </span>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search candidate..."
          aria-label="Search candidate by name"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: 12,
            color: '#0F172A',
            background: 'transparent',
            fontFamily: 'inherit',
            minWidth: 0,
          }}
        />
        {searchQuery ? (
          <button
            type="button"
            onClick={() => {
              setSearchQuery('');
              setOpen(false);
            }}
            aria-label="Clear search"
            style={{
              border: 'none',
              background: '#F1F5F9',
              color: '#64748B',
              width: 20,
              height: 20,
              borderRadius: 10,
              cursor: 'pointer',
              fontSize: 11,
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        ) : null}
      </div>

      {open && searchQuery.trim() ? (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,42,.12)',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 200,
          }}
        >
          {matches.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 12, color: '#64748B' }}>
              No candidates match &ldquo;{searchQuery.trim()}&rdquo;
            </div>
          ) : (
            matches.slice(0, 12).map((session, idx) => {
              const name = normalizeCandidateName(session) || 'Unknown';
              const role = session.job_role ?? session.role ?? '';
              const score = session.overall_score ?? session.score;
              const scoreOutOf10 = score != null && score !== '' ? normalizeScoreOutOf10(score) : null;
              const isActive = idx === activeIndex;
              return (
                <button
                  key={session.id ?? session.session_id ?? `${name}-${idx}`}
                  type="button"
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => selectSession(session)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    borderBottom: '1px solid #F1F5F9',
                    background: isActive ? 'rgba(99,102,241,.08)' : '#fff',
                    cursor: 'pointer',
                    padding: '10px 12px',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>{name}</div>
                  <div style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>
                    {role}
                    {scoreOutOf10 != null && scoreOutOf10 > 0 ? ` · ${scoreOutOf10.toFixed(1)}/10` : ''}
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
